import React from 'react';
import { withStyles, CircularProgress } from '@material-ui/core';
import { compose } from 'recompose';
import { withSnackbar } from 'notistack';
import { observer, inject } from 'mobx-react';
import StopChipList from './StopChipList';
import {
  getUniqueLabelValue,
  getAddressList,
  getCustomerInfoList,
  getCustomerInfoListWithName,
  getDestinationRouteList,
  getListByKey,
  MERGE_TYPES,
  getPreferredRouteNameList,
  getCustomersDeliveryWindowsDetail
} from './utills';
import { withUserProfile } from '../../../../../userProfile/userProfileContext';
import { postData, getDataWithQueryString } from '../../../../../api/componentAPIs';
import { RMAXLogger } from '../../../../../services/LoggerService/RMAXLogger';
import StopProperties from './StopProperties';
import styles from './styles';
import { RadioGroupComponent, MergeStopsActions, DestinationRouteList } from './components';
import { API_ENDPOINTS } from '../../../Reports/DuplicateCustomers/utils';

@inject('IBPlanningStore')
@observer
class MergeStops extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      mergeStopInfo: {
        mergedCustomerId: null,
        customerName: null,
        customerPreferredRouteName: null,
        customerIdForAddress: null,
        customerIdForDeliveryWindow: null,
        duplicateCustomerId: null,
        destinationRouteName: null
      },
      errorInfo: {},
      customers: [],
      isLoadingCustomerDetails: true
    };
  }

  componentDidMount() {
    const customerIds = [
      ...new Set(this.props.selectedStops.map((stop) => stop.originalCustomerCode))
    ];
    this.getCustomerData(customerIds);
    const { selectedStops } = this.props;
    this.destinationRouteList = getDestinationRouteList(selectedStops);
    this.consigneeNameList = getListByKey(selectedStops, 'consigneeName');
    this.addressList = getAddressList(selectedStops);
    this.setState({
      mergeStopInfo: {
        customerName:
          this.consigneeNameList.length === 1 ? this.consigneeNameList[0].value.toString() : null,
        customerIdForAddress:
          this.addressList.length === 1 ? this.addressList[0].value.toString() : null,
        customerPreferredRouteName: null,
        customerIdForDeliveryWindow: null,
        duplicateCustomerId: null,
        destinationRouteName: null
      }
    });
  }

  getCustomerData = (customerCodes) => {
    getDataWithQueryString(
      'GetCustomerDetail',
      `terminalId=${this.props.userData.userProfile.terminal.id}&customerCodes=${customerCodes.join(
        ','
      )}`
    ).then((response) => {
      if (response.status === 200) {
        this.setState(
          {
            customers: response?.data ?? []
          },
          this.updateMergeStopList
        );
      } else {
        this.setState({ isLoadingCustomerDetails: false });
      }
    }).catch(() => {
      this.setState({ isLoadingCustomerDetails: false });
    });
  };

  updateMergeStopList = () => {
    const customerPreferredRouteNameList = getPreferredRouteNameList(
      this.state.customers,
      this.props.selectedStops
    );
    const deliveryWindowList = this.getDeliveryWindowList();
    const customerInfoList = this.filterMiscCustomer();
    this.setState((prevState) => ({
      deliveryWindowList,
      customerPreferredRouteNameList,
      customerInfoList,
      isLoadingCustomerDetails: false,
      mergeStopInfo: {
        ...prevState.mergeStopInfo,
        mergedCustomerId:
          customerInfoList.length === 1 ? customerInfoList[0].value.toString() : null,
        customerIdForDeliveryWindow:
          deliveryWindowList.length === 1 ? deliveryWindowList[0].value.toString() : null,
        customerPreferredRouteName:
          customerPreferredRouteNameList.length === 1
            ? customerPreferredRouteNameList[0].value.toString()
            : null
      }
    }));
  };

  // Checks for errors for each field
  getFieldError = (field, value) => {
    const { isDuplicateCustomerTerminalLevelEnabled } = this.props;

    switch (field) {
      case 'mergedCustomerId':
        return value ? null : '* Customer is required';
      case 'customerName':
        if (isDuplicateCustomerTerminalLevelEnabled) return null;
        return value ? null : '* Customer name is required';
      case 'customerPreferredRouteName':
        if (isDuplicateCustomerTerminalLevelEnabled) return null;
        return this.state.customerPreferredRouteNameList?.length > 0 && !value
          ? '* Customer Preferred RouteName is required'
          : null;
      case 'customerIdForAddress':
        if (isDuplicateCustomerTerminalLevelEnabled) return null;
        return value ? null : '* Address is required';
      case 'customerIdForDeliveryWindow':
        if (isDuplicateCustomerTerminalLevelEnabled) return null;
        return this.state.deliveryWindowList?.length > 0 && !value
          ? '* Delivery Window is required'
          : null;
      case 'destinationRouteName':
        if (isDuplicateCustomerTerminalLevelEnabled) return null;
        return this.destinationRouteList?.length > 1 && !value
          ? '* Destination RouteName is required'
          : null;
      default:
        return null;
    }
  };

  getScenarioLevelRequestData = (mergeStopInfo) => {
    const requestData = mergeStopInfo;
    requestData.terminalId = this.props.userData.userProfile.terminal.id;
    requestData.scenarioId = this.props.userData.userProfile.scenarioID;
    const duplicateCustomer =
      this.props.selectedStops.filter(
        (stop) => stop.consigneeId != Number(requestData.mergedCustomerId)
      ) || [];
    requestData.duplicateCustomerId = duplicateCustomer.map((customer) => customer.consigneeId);
    if (requestData.destinationRouteName === null) {
      requestData.destinationRouteName = this.destinationRouteList[0].value;
    }
    return requestData;
  };

  getTerminalLevelRequestData = (mergeStopInfo) => {
    const terminalId = this.props.userData.userProfile.terminal.id;
    const originalCustomerId = Number(mergeStopInfo.mergedCustomerId);

    const duplicateCustomerIds = this.props.selectedStops
      .filter((stop) => stop.consigneeId !== originalCustomerId)
      .map((customer) => customer.consigneeId);

    return {
      terminalId,
      originalCustomerId,
      duplicateCustomerIds
    };
  };

  getRequestData = (mergeStopInfo) => {
    return this.props.isDuplicateCustomerTerminalLevelEnabled
      ? this.getTerminalLevelRequestData(mergeStopInfo)
      : this.getScenarioLevelRequestData(mergeStopInfo);
  };

  mergeCustomers = () => {
    const errorInfo = this.getErrorInfo();
    const hasErrors = Object.values(errorInfo).some(Boolean);
    if (hasErrors) return;

    this.props.setMergeStopInProgress(true);
    const requestData = this.getRequestData(this.state.mergeStopInfo);
    const endpoint = this.props.isDuplicateCustomerTerminalLevelEnabled
      ? API_ENDPOINTS.MERGE_CUSTOMERS_TERMINAL
      : API_ENDPOINTS.MERGE_CUSTOMERS_SCENARIO;

    postData(endpoint, requestData).then(
      (response) => {
        if (response && Math.trunc(response.status / 100) === 2) {
          this.handleSuccess(response);
          return;
        }
        this.handleError(requestData);
      },
      (error) => {
        this.handleError(requestData);
      }
    );
  };

  handleSuccess = (requestData) => {
    if (this.props.mergeType === MERGE_TYPES.Customers) {
      this.props.onMergeCompleted();
    }

    if (!this.props.isDuplicateCustomerTerminalLevelEnabled) {
      this.props.glEventHub.emit('DispatchEvents', 'RefreshIBStore', [
        {
          showNotification: false,
          selectedRoutesList: this.props.IBPlanningStore.selectedRouteNameList
        }
      ]);
    }

    this.props.setMergeStopInProgress(false);
    this.props.enqueueSnackbar('Stops merged successfully.', {
      variant: 'success'
    });
    RMAXLogger.info('MergeStops', {
      action: 'MergeSuccess',
      selectedStopCount: this.props.selectedStops?.length ?? 0,
      destinationRouteName: requestData?.destinationRouteName
    });
    this.props.onClose();
  };

  handleError = (requestData) => {
    this.props.enqueueSnackbar(`An error occurred while merging stops. Please try again later.`, {
      variant: 'error'
    });
    RMAXLogger.error('MergeStops', {
      action: 'MergeFailure',
      selectedStopCount: this.props.selectedStops?.length ?? 0,
      destinationRouteName: requestData?.destinationRouteName
    });
    this.props.setMergeStopInProgress(false);
  };

  handleSelectUpdate = (name) => (event) => {
    const value = event.value;
    this.setState((state) => ({
      mergeStopInfo: {
        ...state.mergeStopInfo,
        [name]: value
      },
      errorInfo: {
        ...state.errorInfo,
        [name]: this.getFieldError(name, value)
      }
    }));
  };

  handleUpdate = (name) => (event) => {
    const value = event.target.value;
    this.setState((state) => ({
      mergeStopInfo: {
        ...state.mergeStopInfo,
        [name]: value
      },
      errorInfo: {
        ...state.errorInfo,
        [name]: this.getFieldError(name, value)
      }
    }));
  };

  getDeliveryWindowList = () => {
    return getUniqueLabelValue(
      this.props.selectedStops.map((stop) => {
        return getCustomersDeliveryWindowsDetail(this.state.customers, stop);
      })
    );
  };

  // Set Error messages for each field
  getErrorInfo = () => {
    const errorInfo = {};
    Object.keys(this.state.mergeStopInfo).forEach((fieldName) => {
      errorInfo[fieldName] = this.getFieldError(fieldName, this.state.mergeStopInfo[fieldName]);
    });
    this.setState({
      errorInfo
    });
    return errorInfo;
  };

  filterMiscCustomer = () => {
    /*  If there is atleast one coded customer in the selected customers, show only coded customers
    Otherwise show all customers (which will only have miscallaneous customers) */

    let updatedCustomers = [...this.state.customers];
    const { isDuplicateCustomerTerminalLevelEnabled } = this.props;
    
    // Use name (code) format for terminal-level, code only for scenario-level
    const customerInfoList = isDuplicateCustomerTerminalLevelEnabled
      ? getCustomerInfoListWithName(this.props.selectedStops)
      : getCustomerInfoList(this.props.selectedStops);

    // Filtering out coded customers from the customers list
    const codedCustomers = updatedCustomers.filter((c) => !c.deliveryDetails.isMiscCustomer);

    const finalCustomers = codedCustomers.length > 0 ? codedCustomers : updatedCustomers;

    // Getting a final list of customer ids
    const customerIdsSet = new Set(
      finalCustomers.map((customer) => customer.deliveryDetails.customerId)
    );

    // Returning the list of customers as label-value pair using the ids we found.
    return customerInfoList.filter((stop) => customerIdsSet.has(stop.value));
  };

  getScenarioFieldsVisibility = () => {
    const { isDuplicateCustomerTerminalLevelEnabled } = this.props;
    const isScenarioLevel = !isDuplicateCustomerTerminalLevelEnabled;

    return {
      showCustomerName: isScenarioLevel,
      showAddress: isScenarioLevel,
      showCustomerPreferredRoute: isScenarioLevel && this.state.customerPreferredRouteNameList?.length > 0,
      showDestinationRouteList: isScenarioLevel && this.destinationRouteList?.length > 1,
      showDeliveryWindow: isScenarioLevel && this.state.deliveryWindowList?.length > 0
    };
  };

  render() {
    const { classes, selectedStops, mergeType } = this.props;
    const { errorInfo } = this.state;
    const {
      showCustomerName,
      showAddress,
      showCustomerPreferredRoute,
      showDestinationRouteList,
      showDeliveryWindow
    } = this.getScenarioFieldsVisibility();

    return (
      <div className={classes.contentContainer}>
        <div className={`${classes.main} customScroll`}>
          <div className={classes.group}>
            <div>Selected Stops</div>
            <StopChipList customerStops={selectedStops} />
            {mergeType === MERGE_TYPES.Stops && <StopProperties selectedStops={selectedStops} />}
          </div>
          {showCustomerName && (
            <RadioGroupComponent
              name="customerName"
              value={this.state.mergeStopInfo.customerName}
              options={this.consigneeNameList}
              onChange={this.handleUpdate('customerName')}
              label="Customer Name"
              errorInfo={errorInfo}
            />
          )}
          {showAddress && (
            <RadioGroupComponent
              name="customerIdForAddress"
              value={this.state.mergeStopInfo.customerIdForAddress}
              options={this.addressList}
              onChange={this.handleUpdate('customerIdForAddress')}
              label="Address"
              errorInfo={errorInfo}
            />
          )}
          <RadioGroupComponent
            name="mergedCustomerId"
            value={this.state.mergeStopInfo.mergedCustomerId}
            options={this.state.customerInfoList}
            onChange={this.handleUpdate('mergedCustomerId')}
            label="Customer #"
            errorInfo={errorInfo}
          />
          {this.state.isLoadingCustomerDetails && (
            <div className={classes.loadingContainer}>
              <CircularProgress size={14} />
              <span className={classes.loadingText}>Fetching additional customer details...</span>
            </div>
          )}
          {showCustomerPreferredRoute && (
            <RadioGroupComponent
              name="customerPreferredRouteName"
              value={this.state.mergeStopInfo.customerPreferredRouteName}
              options={this.state.customerPreferredRouteNameList}
              onChange={this.handleUpdate('customerPreferredRouteName')}
              label="Customer Preferred Route Name"
              errorInfo={errorInfo}
            />
          )}
          {showDestinationRouteList && (
            <DestinationRouteList
              options={this.destinationRouteList}
              onChange={this.handleSelectUpdate('destinationRouteName')}
              value={this.state.mergeStopInfo.destinationRouteName}
              errorInfo={errorInfo}
              classes={classes}
            />
          )}
          {showDeliveryWindow && (
            <RadioGroupComponent
              name="customerIdForDeliveryWindow"
              value={this.state.mergeStopInfo.customerIdForDeliveryWindow}
              options={this.state.deliveryWindowList}
              onChange={this.handleUpdate('customerIdForDeliveryWindow')}
              label="Delivery Window"
              errorInfo={errorInfo}
            />
          )}
        </div>
        <MergeStopsActions
          mergeStopInProgress={this.props.mergeStopInProgress}
          onClose={this.props.onClose}
          mergeCustomers={this.mergeCustomers}
          classes={classes}
        />
      </div>
    );
  }
}

export default compose(withStyles(styles), withSnackbar, withUserProfile)(MergeStops);
